// Single source of truth for the RBAC permission model (used by client + server + RLS docs).
export const PERMISSION_KEYS = [
    'manage_team', // invite/remove members, set roles & permissions
    'manage_billing', // subscription, seats, branding
    'manage_clients', // create/edit/archive clients + media
    'manage_opportunities', // create/advance/edit auditions & opportunities
    'assign_clients', // assign clients to team members
    'view_all_clients', // see every client (not just assigned)
];
export const PERMISSION_LABELS = {
    manage_team: 'Manage team',
    manage_billing: 'Manage billing & branding',
    manage_clients: 'Manage clients',
    manage_opportunities: 'Manage auditions',
    assign_clients: 'Assign clients',
    view_all_clients: 'View all clients',
};
export const MEMBERSHIP_ROLES = ['owner', 'admin', 'member'];
// Elevated roles implicitly have every permission (mirrors has_perm() in SQL).
export const ROLE_IMPLIES_ALL = ['owner', 'admin'];
/** Sensible default permission set for a freshly invited member. */
export const DEFAULT_MEMBER_PERMISSIONS = {
    manage_clients: true,
    manage_opportunities: true,
};
export function hasPermission(role, permissions, perm) {
    if (ROLE_IMPLIES_ALL.includes(role))
        return true;
    return Boolean(permissions?.[perm]);
}
const impliesAll = (role) => !!role && ROLE_IMPLIES_ALL.includes(role);
/**
 * Who may hand out which workspace access — team edits, removals, and team invites all ask this one
 * question. A caller can only grant what they already hold:
 *  - `owner` is minted and revoked only by an owner (the top role mints its peers);
 *  - `admin` implies EVERY permission, so it is minted and revoked only by an owner or admin — a plain
 *    member with manage_team who could set it would be handing themselves the whole business;
 *  - a plain member leaves owner/admin rows alone, and may newly grant only permissions they hold
 *    themselves. Keys the target ALREADY has don't count, so resaving a full permission set the
 *    caller merely can't see all of stays allowed.
 * Returns why the change is refused, or null when it's allowed. (ISO 27001 A.5.18 / A.8.2)
 */
export function grantRefusal(caller, change) {
    if ((change.role === 'owner' || change.currentRole === 'owner') && caller.role !== 'owner') {
        return 'only an owner can grant or change owner access';
    }
    if ((impliesAll(change.role) || impliesAll(change.currentRole)) && !impliesAll(caller.role)) {
        return 'only an owner or admin can grant or change admin access';
    }
    if (change.permissions && !impliesAll(caller.role)) {
        for (const key of PERMISSION_KEYS) {
            const newlyGranted = change.permissions[key] === true && change.currentPermissions?.[key] !== true;
            if (newlyGranted && !hasPermission(caller.role, caller.permissions, key)) {
                return `you can only grant permissions you hold yourself (${PERMISSION_LABELS[key]})`;
            }
        }
    }
    return null;
}
//# sourceMappingURL=permissions.js.map